import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, isRef as _isRef, withModifiers as _withModifiers, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 1,
  class: "my-3"
}
const _hoisted_3 = { class: "my-2" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = {
  class: "d-flex align-items-center justify-content-center",
  style: {"min-height":"200px"}
}
const _hoisted_7 = ["id"]
const _hoisted_8 = { key: 5 }
const _hoisted_9 = { for: "project-name" }
const _hoisted_10 = { for: "project-description" }
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { key: 2 }

import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatDate, deepParseDates } from '@/utils/date-utils';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { useUserStore } from '@/store/user';
import {
  ProjectsDocument,
  ProjectCreateDocument,
  ProjectType,
} from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BPagination,
  BTable,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { PAGING_ITEMS_PER_PAGE, TRUNCATE_TEXT_LENGTH } from '@/constants';
import { truncateText } from '@/utils/string-utils';
import ProjectStatus from '@/components/project/ProjectStatus.vue';
import { openRouterLink } from '@/utils/router-utils';


export default /*@__PURE__*/_defineComponent({
  __name: 'UserProjectList',
  setup(__props) {

const userStore = useUserStore();
const router = useRouter();

// New Project Form
const newProjectName = ref<string>('');
const newProjectDescription = ref<string>('');
const newProjectModalShow = ref(false);

// Filtering and paging state
const namePattern = ref<string | null>(null);
const descriptionPattern = ref<string | null>(null);
const userRole = ref<string | null>(null);

const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return projectsQueryResult.value?.projects?.total || 0;
});

function clearFilter() {
  namePattern.value = null;
  descriptionPattern.value = null;
  userRole.value = null;
  pagingPageCurrent.value = 1;

  requestRefetchProjects();
}

// --- User Projects ---
const projects = computed<ProjectType[]>(() => {
  return (
    projectsQueryResult.value?.projects?.projects
      ?.filter((prj) => prj && prj !== null)
      .map((prj) => ({
        ...(deepParseDates(prj) as ProjectType),
      })) || []
  );
});

// GraphQL Query to Get Projects
const {
  result: projectsQueryResult,
  loading: projectsQueryInProgress,
  error: projectsQueryError,
  refetch: refetchProjects,
} = useQuery(ProjectsDocument, {
  namePattern: namePattern.value,
  descriptionPattern: descriptionPattern.value,
  userRole: userRole.value,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: pagingItemsPerPage.value,
});

// Function to refetch data
function requestRefetchProjects() {
  refetchProjects({
    namePattern: namePattern.value,
    descriptionPattern: descriptionPattern.value,
    userRole: userRole.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.catch((err) => {
      console.error('Refetch failed:', err);
    })
    .then(() => {
      console.log('Refetched');
    });
}

// GraphQL Mutation to Create Project
const {
  mutate: createProject,
  onDone: onCreateProjectDone,
  onError: onCreateProjectError,
} = useMutation(ProjectCreateDocument);

// Loading and Error States
const loadingCreateProject = ref(false);
const showCreateProjectSuccess = ref(false);
const errorCreateProject = ref<string | null>(null);

// Function to submit new project
async function onSubmitNewProject() {
  if (!newProjectName.value) {
    errorCreateProject.value = 'Project name is required.';
    return;
  }
  errorCreateProject.value = null;
  loadingCreateProject.value = true;
  createProject({
    name: newProjectName.value,
    description: newProjectDescription.value,
  });
}

// Handle the result of the createProject mutation
onCreateProjectDone((res) => {
  if (res.data && res.data.projectCreate) {
    loadingCreateProject.value = false;
    showCreateProjectSuccess.value = true;
    newProjectName.value = '';
    setTimeout(() => {
      showCreateProjectSuccess.value = false;
    }, 2000);
    requestRefetchProjects();
    // TODO: Update Cache, insert new item manually ?
    router.push({
      name: 'ProjectDetail',
      params: { id: res.data.projectCreate.id },
    });
  } else {
    console.error('Project creation failed:', res.data);
    loadingCreateProject.value = false;
    errorCreateProject.value = 'Failed to create project. Please try again.';
  }
});

// Handle errors in the createProject mutation
onCreateProjectError((err) => {
  console.error('Error creating project:', err);
  loadingCreateProject.value = false;
  errorCreateProject.value = 'Failed to create project. Please try again.';
});

// Table Fields
const fields = [
  {
    key: 'createdAt',
    label: 'Created',
    sortable: true,
    formatter: formatDate,
    tdClass: 'align-middle',
  },
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'description',
    label: 'Description',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'role',
    label: 'Role',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'actions',
    label: '',
    tdClass: 'align-middle text-end',
  },
];

// --- Handlers ---
function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchProjects();
  }
}

function getProjectUserRole(project: ProjectType): string {
  return (
    project.usersAssociated?.find(
      (assoc) =>
        assoc &&
        assoc !== null &&
        ((assoc.user && assoc.user.id === userStore.id) ||
          (assoc.userId && assoc.userId === userStore.id))
    )?.userRole || '-'
  );
}

function goToProjectDetail(
  project: ProjectType,
  index?: number,
  evt?: MouseEvent
) {
  openRouterLink(
    {
      name: 'ProjectDetail',
      params: { id: project.id },
      query: {
        returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
        returnLabel: 'Projects',
      },
    },
    router,
    evt
  );
}

function closeNewProjectModal() {
  newProjectModalShow.value = false;
  newProjectName.value = '';
  newProjectDescription.value = '';
  errorCreateProject.value = null;
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchProjects();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (_unref(userStore).noOrganizationSelected && !_unref(projectsQueryInProgress))
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_unref(BAlert), {
            modelValue: _unref(userStore).noOrganizationSelected,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(userStore).noOrganizationSelected) = $event)),
            variant: "warning"
          }, {
            default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
              _createTextVNode(" You are not associated with any organization. Please contact your account manager or organization administrator to get access to the upload features. ", -1)
            ]))]),
            _: 1
          }, 8, ["modelValue"])
        ]))
      : (_unref(userStore).activeOrgAssociation)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(_unref(BButton), {
              type: "submit",
              variant: "outline-dark",
              class: "flex-shrink-0",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (newProjectModalShow.value = true)),
              disabled: loadingCreateProject.value
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(FontAwesomeIcon), { icon: ['fa', 'plus'] }),
                _cache[15] || (_cache[15] = _createTextVNode(" Create Project ", -1))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]))
        : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      modelValue: _unref(projectsQueryError),
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(projectsQueryError) ? (projectsQueryError).value = $event : null)),
      variant: "danger",
      dismissible: "",
      class: "mt-3"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(projectsQueryError)) + " ", 1),
        _createVNode(_unref(BButton), {
          onClick: requestRefetchProjects,
          variant: "outline-dark",
          class: "btn-sm"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FontAwesomeIcon), { icon: ['fa', 'sync'] }),
            _cache[16] || (_cache[16] = _createTextVNode(" Retry", -1))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(BForm), {
        class: "d-flex flex-row align-items-end flex-wrap gap-2",
        onSubmit: _withModifiers(onSearchRequested, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(BFormGroup), {
            label: "Project name",
            "label-for": "project-name"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                id: "project-name",
                type: "text",
                modelValue: namePattern.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((namePattern).value = $event)),
                placeholder: "e.g. My Project"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Description",
            "label-for": "description-pattern"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                id: "description-pattern",
                type: "text",
                modelValue: descriptionPattern.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((descriptionPattern).value = $event)),
                placeholder: "e.g. CD34+"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Role",
            "label-for": "role"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                id: "role",
                type: "text",
                modelValue: userRole.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((userRole).value = $event)),
                placeholder: "e.g. owner"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BButton), {
            variant: "outline-secondary",
            onClick: clearFilter
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(FontAwesomeIcon), {
                icon: "trash",
                class: "me-1"
              }),
              _cache[17] || (_cache[17] = _createTextVNode(" Clear filter ", -1))
            ]),
            _: 1
          }),
          _createVNode(_unref(BButton), {
            variant: "outline-dark",
            type: "submit",
            disabled: _unref(projectsQueryInProgress)
          }, {
            default: _withCtx(() => [
              (_unref(projectsQueryInProgress))
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                    _createVNode(_unref(BSpinner), {
                      small: "",
                      label: ""
                    }),
                    _cache[18] || (_cache[18] = _createTextVNode(" Searching ", -1))
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_5, [
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "magnifying-glass",
                      class: "me-1"
                    }),
                    _cache[19] || (_cache[19] = _createTextVNode(" Refresh ", -1))
                  ]))
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        _: 1
      })
    ]),
    (pagingTotalRows.value > pagingItemsPerPage.value)
      ? (_openBlock(), _createBlock(_unref(BPagination), {
          key: 2,
          modelValue: pagingPageCurrent.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((pagingPageCurrent).value = $event)),
          "total-rows": pagingTotalRows.value,
          "per-page": pagingItemsPerPage.value,
          pills: "",
          size: "sm"
        }, null, 8, ["modelValue", "total-rows", "per-page"]))
      : _createCommentVNode("", true),
    (!_unref(projectsQueryError))
      ? (_openBlock(), _createBlock(_unref(BCard), {
          key: 3,
          class: "my-3"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BTable), {
              striped: "",
              responsive: "",
              hover: "",
              "show-empty": "",
              class: "browser-table",
              items: projects.value,
              fields: fields,
              busy: _unref(projectsQueryInProgress),
              onRowClicked: goToProjectDetail
            }, {
              "table-busy": _withCtx(() => [
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    class: "align-middle me-1"
                  }),
                  _cache[20] || (_cache[20] = _createElementVNode("span", { class: "ms-1" }, "Loading projects...", -1))
                ])
              ]),
              empty: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                _createElementVNode("div", { class: "text-center my-5" }, [
                  _createElementVNode("p", { class: "text-muted" }, "No projects found.")
                ], -1)
              ]))]),
              "cell(name)": _withCtx((data) => [
                _createElementVNode("strong", null, _toDisplayString(data.value), 1)
              ]),
              "cell(description)": _withCtx((data) => [
                _createElementVNode("span", {
                  id: 'project-description-' + data.item.id
                }, _toDisplayString(_unref(truncateText)(data.value, _unref(TRUNCATE_TEXT_LENGTH))), 9, _hoisted_7),
                (data.value && data.value.length > _unref(TRUNCATE_TEXT_LENGTH))
                  ? (_openBlock(), _createBlock(_unref(BTooltip), {
                      key: 0,
                      target: 'project-description-' + data.item.id,
                      class: "large-tooltip"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(data.value), 1)
                      ]),
                      _: 2
                    }, 1032, ["target"]))
                  : _createCommentVNode("", true)
              ]),
              "cell(status)": _withCtx((data) => [
                _createVNode(ProjectStatus, {
                  project: data.item
                }, null, 8, ["project"])
              ]),
              "cell(role)": _withCtx((data) => [
                _createTextVNode(_toDisplayString(getProjectUserRole(data.item)), 1)
              ]),
              "cell(actions)": _withCtx((data) => [
                _createVNode(_unref(BButton), {
                  variant: "outline-dark",
                  class: "btn-sm",
                  onClick: [
                    ($event: any) => (goToProjectDetail(data.item, data.item.id, $event)),
                    _cache[7] || (_cache[7] = _withModifiers(() => {}, ["stop"]))
                  ]
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(FontAwesomeIcon), { icon: ['fa', 'eye'] })
                  ]),
                  _: 1
                }, 8, ["onClick"])
              ]),
              _: 1
            }, 8, ["items", "busy"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (pagingTotalRows.value > pagingItemsPerPage.value)
      ? (_openBlock(), _createBlock(_unref(BPagination), {
          key: 4,
          modelValue: pagingPageCurrent.value,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((pagingPageCurrent).value = $event)),
          "total-rows": pagingTotalRows.value,
          "per-page": pagingItemsPerPage.value,
          pills: "",
          size: "sm"
        }, null, 8, ["modelValue", "total-rows", "per-page"]))
      : _createCommentVNode("", true),
    (newProjectModalShow.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createVNode(_unref(BModal), {
            modelValue: newProjectModalShow.value,
            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((newProjectModalShow).value = $event)),
            id: "create-project-modal",
            onHide: closeNewProjectModal
          }, {
            title: _withCtx(() => [
              _createVNode(_unref(FontAwesomeIcon), {
                icon: ['fa', 'diagram-project'],
                class: "me-2"
              }),
              _cache[22] || (_cache[22] = _createTextVNode(" Create New Project ", -1))
            ]),
            footer: _withCtx(() => [
              _createVNode(_unref(BButton), {
                variant: "secondary",
                onClick: closeNewProjectModal,
                disabled: loadingCreateProject.value
              }, {
                default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                  _createTextVNode("Cancel", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_unref(BButton), {
                variant: "primary",
                disabled: loadingCreateProject.value,
                onClick: onSubmitNewProject
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(FontAwesomeIcon), {
                    icon: ['fa', 'plus'],
                    class: "me-1"
                  }),
                  (loadingCreateProject.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                        _createVNode(_unref(BSpinner), {
                          small: "",
                          label: ""
                        }),
                        _cache[26] || (_cache[26] = _createTextVNode(" Creating Project ", -1))
                      ]))
                    : (showCreateProjectSuccess.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_12, [
                          _cache[27] || (_cache[27] = _createTextVNode(" Project Created ", -1)),
                          _createVNode(_unref(FontAwesomeIcon), {
                            icon: ['fa', 'check-double'],
                            class: "me-2"
                          })
                        ]))
                      : (_openBlock(), _createElementBlock("span", _hoisted_13, " Create Project "))
                ]),
                _: 1
              }, 8, ["disabled"])
            ]),
            default: _withCtx(() => [
              _createElementVNode("div", null, [
                _createVNode(_unref(BFormGroup), null, {
                  default: _withCtx(() => [
                    _createElementVNode("label", _hoisted_9, [
                      _createVNode(_unref(FontAwesomeIcon), {
                        icon: ['fa', 'tag'],
                        class: "me-1"
                      }),
                      _cache[23] || (_cache[23] = _createTextVNode(" Project Name ", -1))
                    ]),
                    _createVNode(_unref(BFormInput), {
                      id: "project-name",
                      modelValue: newProjectName.value,
                      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((newProjectName).value = $event)),
                      required: ""
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                  default: _withCtx(() => [
                    _createElementVNode("label", _hoisted_10, [
                      _createVNode(_unref(FontAwesomeIcon), {
                        icon: ['fa', 'file-lines'],
                        class: "me-1"
                      }),
                      _cache[24] || (_cache[24] = _createTextVNode(" Description ", -1))
                    ]),
                    _createVNode(_unref(BFormTextarea), {
                      id: "project-description",
                      modelValue: newProjectDescription.value,
                      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((newProjectDescription).value = $event)),
                      rows: "3"
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(BAlert), {
                  modelValue: errorCreateProject.value,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((errorCreateProject).value = $event)),
                  variant: "danger",
                  dismissible: "",
                  class: "mt-3",
                  onDismissed: _cache[12] || (_cache[12] = ($event: any) => (errorCreateProject.value = null))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(errorCreateProject.value), 1)
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ])
            ]),
            _: 1
          }, 8, ["modelValue"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})